import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson } from '@factorialco/f0-react'
import { ProjectionKind, useCustomTemplatesDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ReviewTypeTag } from '../ReviewTypeTag'
import { Actions } from './Actions'
import authorFilter from './filters/authorFilter'
import createdOnFilter from './filters/createdOnFilter'

type Props = {
  canCreateTemplates?: boolean
}

export const CustomTemplatesDataTable: React.FC<Props> = ({ canCreateTemplates }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const source = useCustomTemplatesDataTable(
    [
      {
        identifier: 'name',
        title: i18n.t('performance_process.reviews.datatable.name'),
        fixed: true,
        template: '{{ name }}',
        kind: ProjectionKind.Custom,
        colSpan: 2,
        render: ({ name }) => {
          return <Text.Normal>{name}</Text.Normal>
        },
        sortBy: 'name',
      },
      {
        identifier: 'reviewerStrategies',
        title: i18n.t('performance_process.reviews.launch_review_types'),
        fixed: true,
        template: '{{ reviewerStrategies }}',
        kind: ProjectionKind.Custom,
        colSpan: 2,
        render: ({ id, reviewerStrategies }) => {
          return (
            <Box flexDirection="row" flexWrap="wrap" gap="s8">
              {reviewerStrategies?.length
                ? reviewerStrategies.map((type) => (
                    <ReviewTypeTag type={type} key={`${id}-${type}`} />
                  ))
                : '-'}
            </Box>
          )
        },
      },
      {
        identifier: 'created_on',
        title: i18n.t('performance_process.reviews.datatable.created_on'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ createdAt }}',
        render: ({ createdAt }) => {
          return (
            <Box width="s96">
              {createdAt ? <Text.Normal>{dateBackendDateTimeToUI(createdAt)}</Text.Normal> : '-'}
            </Box>
          )
        },
        sortBy: 'createdAt',
      },
      {
        identifier: 'author',
        title: i18n.t('common.author'),
        fixed: true,
        template: '{{ author }}',
        kind: ProjectionKind.Custom,
        width: 100,
        render: ({ author }) => {
          return (
            author && (
              <F0AvatarPerson
                src={buildUrl(author.avatar)}
                firstName={author.firstName}
                lastName={author.firstName}
                size="small"
              />
            )
          )
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 100,
        fixed: true,
        render: (template) => {
          return (
            <Actions
              template={template}
              canManage={template._policies?.manage?.can ?? false}
              canDuplicate={canCreateTemplates ?? false}
            />
          )
        },
      },
    ],
    { variables: { ids: [], search: debouncedSearch } }
  )

  const filters = [authorFilter(source), createdOnFilter(source)]

  return (
    <DataTable.Provider
      source={source}
      sort={{
        path: 'name',
        direction: 'asc',
      }}
      filters={filters}
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableExport
          disableEditColumns
          search={{
            placeholder: i18n.t('common.search_by'),
            value: search,
            onChange: onSearch,
          }}
        />
        <DataTable.Table />
      </Box>
    </DataTable.Provider>
  )
}
